import React, { useState, useEffect } from 'react';
import './LabelGenerator.css';
import apiService from '../../services/api';
import labelPrintService from '../../services/labelPrintService';
import dateUtils from '../../utils/dateUtils';


export default function LabelGenerator({ isOpen, onClose, initialProducts = null }) {
    const [products, setProducts] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [showSearchResults, setShowSearchResults] = useState(false);
    const [isLoadingProducts, setIsLoadingProducts] = useState(false);

    // Form state
    const [formData, setFormData] = useState({
        numberOfLabels: 1,
        packingDate: dateUtils.getChicagoISOString().split('T')[0],
        sellingPriceGroup: 'default'
    });

    // Label sheet size options
    const LABEL_SIZES = [
        { id: '20_per_sheet', label: '20 Labels per Sheet, Sheet Size: 8.5" x 11", Label Size: 4" x 1", Labels per sheet: 20', pageSize: '8.5in 11in', labelW: '4in', labelH: '1in', cols: 2 },
        { id: '30_per_sheet', label: '30 Labels per sheet, Sheet Size: 8.5" x 11", Label Size: 2.625" x 1", Labels per sheet: 30', pageSize: '8.5in 11in', labelW: '2.625in', labelH: '1in', cols: 3 },
        { id: '32_per_sheet', label: '32 Labels per sheet, Sheet Size: 8.5" x 11", Label Size: 2" x 1.25", Labels per sheet: 32', pageSize: '8.5in 11in', labelW: '2in', labelH: '1.25in', cols: 4 },
        { id: '40_per_sheet', label: '40 Labels per sheet, Sheet Size: 8.5" x 11", Label Size: 2" x 1", Labels per sheet: 40', pageSize: '8.5in 11in', labelW: '2in', labelH: '1in', cols: 4 },
        { id: '50_per_sheet', label: '50 Labels per Sheet, Sheet Size: 8.5" x 11", Label Size: 1.5" x 1", Labels per sheet: 50', pageSize: '8.5in 11in', labelW: '1.5in', labelH: '1in', cols: 5 },
        { id: 'continuous_roll', label: 'Continuous Rolls - 31.75mm x 25.4mm, Label Size: 31.75mm x 25.4mm, Gap: 3.18mm', pageSize: '31.75mm 25.4mm', labelW: '31.75mm', labelH: '25.4mm', cols: 1 },
    ];
    const [selectedLabelSize, setSelectedLabelSize] = useState(LABEL_SIZES[0].id);

    // Label display options
    const [labelOptions, setLabelOptions] = useState({
        showProductName: true,
        productNameSize: 15,
        showVariation: true,
        variationSize: 17,
        showPrice: true,
        priceSize: 17,
        priceIncTax: true,
        showBusinessName: true,
        businessNameSize: 20,
        showPackingDate: true,
        packingDateSize: 12
    });

    // Load initial products (e.g. from a purchase)
    useEffect(() => {
        if (initialProducts && Array.isArray(initialProducts) && initialProducts.length > 0) {
            const mapped = initialProducts.map(p => ({
                id: p.id,
                name: p.name,
                sku: p.sku,
                price: p.price || 0,
                priceExcTax: p.priceExcTax || p.price || 0,
                variation: p.variation || 'Default',
                numberOfLabels: p.numberOfLabels || 1,
                barcode: p.barcode || p.sku || ''
            }));
            setSelectedProducts(mapped);
        } else if (initialProducts && !Array.isArray(initialProducts)) {
            // Backward compatibility for single object
            setSelectedProducts([{
                ...initialProducts,
                numberOfLabels: initialProducts.numberOfLabels || 1,
                variation: initialProducts.variation || 'Default',
                priceExcTax: initialProducts.priceExcTax || initialProducts.price || 0,
                barcode: initialProducts.barcode || initialProducts.sku || ''
            }]);
        }
    }, [initialProducts]);

    // Search products
    const handleSearchProducts = async (term) => {
        setSearchTerm(term);
        if (term.length < 2) {
            setProducts([]);
            setShowSearchResults(false);
            return;
        }

        setIsLoadingProducts(true);
        try {
            const data = await apiService.getProducts({ search: term, per_page: 10 });
            const productList = Array.isArray(data) ? data : (data.data || []);

            const mappedProducts = productList.map(p => {
                const firstVariation = p.product_variations?.[0]?.variations?.[0];
                return {
                    id: p.id,
                    name: p.name,
                    sku: p.sku,
                    price: firstVariation ? parseFloat(firstVariation.sell_price_inc_tax) : 0,
                    priceExcTax: firstVariation ? parseFloat(firstVariation.sell_price_exc_tax) : 0,
                    variation: firstVariation?.name || 'Default',
                    barcode: p.sku || '',
                    unit: p.unit?.actual_name || 'Single'
                };
            });

            setProducts(mappedProducts);
            setShowSearchResults(true);
        } catch (error) {
            console.error('Error searching products:', error);
        } finally {
            setIsLoadingProducts(false);
        }
    };

    const handleSelectProduct = (product) => {
        if (!selectedProducts.find(p => p.id === product.id)) {
            setSelectedProducts([...selectedProducts, { ...product, numberOfLabels: 1 }]);
        }
        setSearchTerm('');
        setShowSearchResults(false);
    };

    const handleRemoveProduct = (productId) => {
        setSelectedProducts(selectedProducts.filter(p => p.id !== productId));
    };

    const handleUpdateProductLabels = (productId, count) => {
        setSelectedProducts(selectedProducts.map(p =>
            p.id === productId ? { ...p, numberOfLabels: parseInt(count) || 1 } : p
        ));
    };

    const handlePrint = () => {
        const sizeConfig = LABEL_SIZES.find(s => s.id === selectedLabelSize) || LABEL_SIZES[0];
        
        // Build HTML content for labels
        let labelsHTML = '';
        selectedProducts.forEach(product => {
            for (let i = 0; i < product.numberOfLabels; i++) {
                labelsHTML += `
                    <div class="label-print">
                        ${labelOptions.showBusinessName ? `<div class="label-business-name" style="font-size: ${labelOptions.businessNameSize}px; font-weight: bold; margin-bottom: 2px;">Lefty's Liquor</div>` : ''}
                        ${labelOptions.showProductName ? `<div class="label-product-name" style="font-size: ${labelOptions.productNameSize}px; font-weight: 600; margin-bottom: 2px;">${product.name || 'N/A'}</div>` : ''}
                        ${labelOptions.showVariation ? `<div class="label-variation" style="font-size: ${labelOptions.variationSize}px; margin-bottom: 2px;">${product.variation || 'Default'}</div>` : ''}
                        ${labelOptions.showPrice ? `<div class="label-price" style="font-size: ${labelOptions.priceSize}px; font-weight: bold; margin-bottom: 4px;">$${(labelOptions.priceIncTax ? (product.price || 0) : (product.priceExcTax || 0)).toFixed(2)}</div>` : ''}
                        ${labelOptions.showPackingDate ? `<div class="label-packing-date" style="font-size: ${labelOptions.packingDateSize}px; margin-bottom: 4px;">Packed: ${formData.packingDate || 'N/A'}</div>` : ''}
                        <div class="label-barcode">
                            <svg id="barcode-${product.id}-${i}" style="max-height: 45px;"></svg>
                        </div>
                    </div>
                `;
            }
        });
        
        // Create a hidden iframe for more stable printing
        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.left = '-9999px';
        iframe.style.top = '0';
        iframe.style.width = '100%';
        iframe.style.height = '100%';
        iframe.style.border = 'none';
        iframe.setAttribute('aria-hidden', 'true');
        document.body.appendChild(iframe);

        const doc = iframe.contentDocument;
        if (!doc) {
            console.error('Failed to create print iframe');
            return;
        }

        doc.open();
        doc.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>Print Labels</title>
                <style>
                    * { margin: 0; padding: 0; box-sizing: border-box; }
                    body { margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; }
                    .label-print {
                        width: ${sizeConfig.labelW};
                        height: ${sizeConfig.labelH};
                        border: 1px solid #000;
                        padding: 0.1in;
                        display: inline-block;
                        vertical-align: top;
                        margin: 0.05in;
                        text-align: center;
                        page-break-inside: avoid;
                        overflow: visible !important;
                        position: relative;
                        background: #fff;
                    }
                    .label-business-name, .label-product-name, .label-variation, 
                    .label-price, .label-packing-date {
                        display: block;
                        line-height: 1.1;
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                    }
                    .label-barcode { 
                        width: 100%;
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        margin-top: 2px;
                    }
                    .label-barcode svg { 
                        width: 100%; 
                        height: auto; 
                        display: block;
                    }
                    @page { size: ${sizeConfig.pageSize}; margin: 0.25in; }
                    @media print {
                        body { margin: 0; padding: 0; }
                    }
                </style>
                <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
            </head>
            <body>
                <div style="display: flex; flex-wrap: wrap; justify-content: flex-start;">
                    ${labelsHTML}
                </div>
                <script>
                    window.onload = function() {
                        // Check if JsBarcode is loaded
                        if (typeof JsBarcode === 'undefined') {
                            console.error('JsBarcode library failed to load');
                            // Fallback to print anyway or show error
                            window.print();
                            return;
                        }

                        // Generate all barcodes
                        ${selectedProducts.map(product => 
                            Array.from({ length: product.numberOfLabels }).map((_, i) => 
                                `try {
                                    JsBarcode("#barcode-${product.id}-${i}", "${product.barcode || product.sku}", {
                                        format: "CODE128",
                                        width: 1.5,
                                        height: 35,
                                        displayValue: true,
                                        fontSize: 10,
                                        margin: 2
                                    });
                                } catch(e) { console.error('Barcode failed', e); }`
                            ).join('\n')
                        ).join('\n')}
                        
                        // Wait a small bit for barcodes to render then print
                        setTimeout(function() {
                            window.print();
                            // Cleanup after a delay
                            setTimeout(function() {
                                window.parent.document.body.removeChild(window.frameElement);
                            }, 500);
                        }, 500);
                    };
                </script>
            </body>
            </html>
        `);
        doc.close();
    };

    if (!isOpen) return null;

    return (
        <div className="label-generator-overlay">
            <div className="label-generator-modal">
                <div className="label-generator-header">
                    <h2>Generate Product Labels</h2>
                    <button className="close-btn" onClick={onClose}>×</button>
                </div>

                <div className="label-generator-body">
                    {/* Product Search Section */}
                    <div className="form-section">
                        <h3>Select Products</h3>
                        <div className="product-search-container">
                            <input
                                type="text"
                                placeholder="Enter product name to search..."
                                value={searchTerm}
                                onChange={(e) => handleSearchProducts(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && searchTerm.trim()) {
                                        e.preventDefault();
                                        // If there's an exact SKU/Barcode match, select it
                                        const exactMatch = products.find(p => 
                                            (p.sku || '').toLowerCase() === searchTerm.toLowerCase().trim() ||
                                            (p.barcode || '').toLowerCase() === searchTerm.toLowerCase().trim()
                                        );
                                        if (exactMatch) {
                                            handleSelectProduct(exactMatch);
                                        } else if (products.length === 1) {
                                            // If only one result, select it
                                            handleSelectProduct(products[0]);
                                        }
                                    }
                                }}
                                className="product-search-input"
                                autoFocus
                            />
                            {isLoadingProducts && <div className="search-loading">Searching...</div>}
                            {showSearchResults && products.length > 0 && (
                                <div className="search-results">
                                    {products.map(product => (
                                        <div
                                            key={product.id}
                                            className="search-result-item"
                                            onClick={() => handleSelectProduct(product)}
                                            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                                        >
                                            <div>
                                                <strong>{product.name}</strong>
                                                <br />
                                                <small className="product-sku">SKU: {product.sku}</small>
                                            </div>
                                            <div style={{ textAlign: 'right' }}>
                                                <span className="product-price" style={{ fontWeight: '600', color: '#10b981' }}>
                                                    ${product.price?.toFixed(2)}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Selected Products Table */}
                        {selectedProducts.length > 0 && (
                            <div className="selected-products-table">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Product</th>
                                            <th>Price</th>
                                            <th>No. of Labels</th>
                                            <th>Packing Date</th>
                                            <th>Price Group</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedProducts.map(product => (
                                            <tr key={product.id}>
                                                <td>
                                                    <strong>{product.name}</strong>
                                                    <br />
                                                    <small>SKU: {product.sku}</small>
                                                </td>
                                                <td style={{ fontWeight: 600 }}>
                                                    ${product.price?.toFixed(2)}
                                                </td>
                                                <td>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        value={product.numberOfLabels}
                                                        onChange={(e) => handleUpdateProductLabels(product.id, e.target.value)}
                                                        className="label-count-input"
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="date"
                                                        value={formData.packingDate}
                                                        onChange={(e) => setFormData({ ...formData, packingDate: e.target.value })}
                                                        className="date-input"
                                                    />
                                                </td>
                                                <td>
                                                    <select
                                                        value={formData.sellingPriceGroup}
                                                        onChange={(e) => setFormData({ ...formData, sellingPriceGroup: e.target.value })}
                                                        className="price-group-select"
                                                    >
                                                        <option value="default">Default</option>
                                                        <option value="wholesale">Wholesale</option>
                                                        <option value="retail">Retail</option>
                                                    </select>
                                                </td>
                                                <td>
                                                    <button
                                                        className="remove-product-btn"
                                                        onClick={() => handleRemoveProduct(product.id)}
                                                    >
                                                        Remove
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* Label Information Settings */}
                    <div className="form-section">
                        <h3>Information to Show in Labels</h3>
                        <div className="label-options-grid">
                            <div className="label-option">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={labelOptions.showProductName}
                                        onChange={(e) => setLabelOptions({ ...labelOptions, showProductName: e.target.checked })}
                                    />
                                    Product Name
                                </label>
                                <input
                                    type="number"
                                    value={labelOptions.productNameSize}
                                    onChange={(e) => setLabelOptions({ ...labelOptions, productNameSize: parseInt(e.target.value) })}
                                    className="size-input"
                                    placeholder="Size"
                                />
                            </div>

                            <div className="label-option">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={labelOptions.showVariation}
                                        onChange={(e) => setLabelOptions({ ...labelOptions, showVariation: e.target.checked })}
                                    />
                                    Product Variation
                                </label>
                                <input
                                    type="number"
                                    value={labelOptions.variationSize}
                                    onChange={(e) => setLabelOptions({ ...labelOptions, variationSize: parseInt(e.target.value) })}
                                    className="size-input"
                                    placeholder="Size"
                                />
                            </div>

                            <div className="label-option">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={labelOptions.showPrice}
                                        onChange={(e) => setLabelOptions({ ...labelOptions, showPrice: e.target.checked })}
                                    />
                                    Product Price
                                </label>
                                <input
                                    type="number"
                                    value={labelOptions.priceSize}
                                    onChange={(e) => setLabelOptions({ ...labelOptions, priceSize: parseInt(e.target.value) })}
                                    className="size-input"
                                    placeholder="Size"
                                />
                                <select
                                    value={labelOptions.priceIncTax ? 'inc' : 'exc'}
                                    onChange={(e) => setLabelOptions({ ...labelOptions, priceIncTax: e.target.value === 'inc' })}
                                    className="tax-select"
                                >
                                    <option value="inc">Inc. tax</option>
                                    <option value="exc">Exc. tax</option>
                                </select>
                            </div>

                            <div className="label-option">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={labelOptions.showBusinessName}
                                        onChange={(e) => setLabelOptions({ ...labelOptions, showBusinessName: e.target.checked })}
                                    />
                                    Business Name
                                </label>
                                <input
                                    type="number"
                                    value={labelOptions.businessNameSize}
                                    onChange={(e) => setLabelOptions({ ...labelOptions, businessNameSize: parseInt(e.target.value) })}
                                    className="size-input"
                                    placeholder="Size"
                                />
                            </div>

                            <div className="label-option">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={labelOptions.showPackingDate}
                                        onChange={(e) => setLabelOptions({ ...labelOptions, showPackingDate: e.target.checked })}
                                    />
                                    Print Packing Date
                                </label>
                                <input
                                    type="number"
                                    value={labelOptions.packingDateSize}
                                    onChange={(e) => setLabelOptions({ ...labelOptions, packingDateSize: parseInt(e.target.value) })}
                                    className="size-input"
                                    placeholder="Size"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Barcode Settings */}
                    <div className="form-section">
                        <h3>Barcode Settings</h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#374151' }}>Label Size / Sheet Format:</label>
                            <select
                                value={selectedLabelSize}
                                onChange={(e) => setSelectedLabelSize(e.target.value)}
                                style={{
                                    padding: '0.55rem 0.75rem',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '6px',
                                    fontSize: '0.85rem',
                                    color: '#1e293b',
                                    background: '#fff',
                                    cursor: 'pointer',
                                    width: '100%',
                                    fontFamily: 'inherit'
                                }}
                            >
                                {LABEL_SIZES.map(s => (
                                    <option key={s.id} value={s.id}>{s.label}</option>
                                ))}
                            </select>
                            <p style={{ margin: '0.25rem 0 0', fontSize: '0.78rem', color: '#6b7280' }}>
                                {LABEL_SIZES.find(s => s.id === selectedLabelSize)?.label}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="label-generator-footer">
                    <button className="btn-secondary" onClick={onClose}>Cancel</button>
                    <button className="btn-primary" onClick={handlePrint} disabled={selectedProducts.length === 0}>
                        Print Labels
                    </button>
                </div>

                {/* Print Preview (hidden on screen, visible when printing) */}
                <div className="print-only">
                    {selectedProducts.map(product =>
                        Array.from({ length: product.numberOfLabels }).map((_, index) => (
                            <div key={`${product.id}-${index}`} className="label-print">
                                {labelOptions.showBusinessName && (
                                    <div className="label-business-name" style={{ fontSize: `${labelOptions.businessNameSize}px` }}>
                                        Lefty's Liquor
                                    </div>
                                )}
                                {labelOptions.showProductName && (
                                    <div className="label-product-name" style={{ fontSize: `${labelOptions.productNameSize}px` }}>
                                        {product.name}
                                    </div>
                                )}
                                {labelOptions.showVariation && (
                                    <div className="label-variation" style={{ fontSize: `${labelOptions.variationSize}px` }}>
                                        {product.variation}
                                    </div>
                                )}
                                {labelOptions.showPrice && (
                                    <div className="label-price" style={{ fontSize: `${labelOptions.priceSize}px` }}>
                                        ${labelOptions.priceIncTax ? product.price.toFixed(2) : product.priceExcTax.toFixed(2)}
                                    </div>
                                )}
                                {labelOptions.showPackingDate && (
                                    <div className="label-packing-date" style={{ fontSize: `${labelOptions.packingDateSize}px` }}>
                                        Packed: {formData.packingDate}
                                    </div>
                                )}
                                <div className="label-barcode">
                                    <svg id={`barcode-${product.id}-${index}`}></svg>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
}
